import React, { useEffect, useState } from 'react';
import {
  X,
  BarChart3,
  TrendingUp,
  TrendingDown,
  Users,
  Star,
  MessageCircle,
  Eye,
  Heart,
  Bookmark,
  MapPin,
  Calendar,
  Settings,
  CreditCard,
  Bell,
  Download,
  Filter,
  Search,
  ChevronRight,
  Plus,
  Edit,
  MoreHorizontal,
  CheckCircle,
  AlertCircle,
  Clock,
  DollarSign,
  Building,
  Delete,
  Trash,
  Check,
  Crown,
  PowerOff,
  PowerIcon
} from 'lucide-react';
import { BusinessAnalytics, ReviewTrend, BusinessLocation, Business, Organization, MembershipPlan } from '../types';
import api, { orgAPI, paymentAPI, reviewAPI } from '../services/api';
import LocationModal from './LocationModal';
import AddBusinessLocationModal from './AddBusinessLocationModal';
import AlertModal from './AlertModal';
import CheckoutPayment from './PaymentModal';
import CheckoutForm from './PaymentModal';

interface BusinessDashboardProps {
  organization: Organization;
  onClose: () => void;
  onUpdateProfile: (updates: Partial<Organization>) => void;
}

const formatPlanCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);

const formatPlanLocationLimit = (plan: MembershipPlan) => {
  if (plan.maxLocations === 'unlimited' || plan.maxLocations === null) {
    return 'Unlimited locations';
  }

  const maxLocations = Number(plan.maxLocations);
  return `${maxLocations} location${maxLocations === 1 ? '' : 's'} max`;
};

const calculatePlanMonthlyEstimate = (plan: MembershipPlan, locationCount: number) => {
  if (plan.maxLocations === 'unlimited' || plan.maxLocations === null) {
    return plan.pricePerLocation * locationCount;
  }

  return plan.pricePerLocation * Math.min(locationCount, plan.maxLocations);
};

const BusinessDashboard: React.FC<BusinessDashboardProps> = ({
  organization,
  onClose,
  onUpdateProfile
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'analytics' | 'reviews' | 'locations' | 'settings'>('overview');
  const [dateRange, setDateRange] = useState<'7d' | '30d' | '90d' | '1y'>('30d');

  const [alertModal, setAlertModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    variant?: 'success' | 'error' | 'info';
    confirmLabel?: string;
    onButton?: () => void;
  }>({
    open: false,
    title: '',
    message: ''
  });

  // Use reviews from businessMember prop
  const [allReviews, setAllReviews] = useState<any[]>([]);
  const [summaryReview, setSummaryReview] = useState(null);

  // Update organization profile state
  const [orgProfile, setOrgProfile] = useState<Organization>(organization);

  // Photo lightbox state
  const [activePhoto, setActivePhoto] = useState<{ url: string; reviewId: string } | null>(null);

  // Review section - response review
  const [responseModal, setResponseModal] = useState<{ open: boolean; review: any | null; text: string }>({
    open: false,
    review: null,
    text: '',
  });
  const [savingResponse, setSavingResponse] = useState(false);

  // Review section - apply rating filter
  const [ratingFilter, setRatingFilter] = useState<'all' | '5' | '4' | '3' | '2' | '1'>('all');
  const filteredReviews = ratingFilter === 'all'
    ? allReviews
    : allReviews.filter(r => Number(r.rating) === Number(ratingFilter));

  // Analytics section - location filter
  const [locationFilter, setLocationFilter] = useState<'all' | string>('all');
  const [analyticsPeriod, setAnalyticsPeriod] = useState<'all' | '7d' | '30d' | '90d' | '1y'>('all');
  const [analyticsStats, setAnalyticsStats] = useState<any>(null);

  // Location section - create / edit / delete business location
  const [showAddBusinessModal, setShowAddBusinessModal] = useState(false);
  const [editingBusiness, setEditingBusiness] = useState<Business | null>(null);
  const [deleteBusiness, setDeleteBusiness] = useState<Business | null>(null);
  const [showDeleteBusiness, setShowDeleteBusiness] = useState(false);

  // Setting section - account info / upgrade plan
  const [accountForm, setAccountForm] = useState({
    orgName: '',
    ownerName: '',
    email: '',
    phone: '',
    description: '',
  });
  const [savingAccount, setSavingAccount] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedUpgradePlan, setSelectedUpgradePlan] = useState<string | null>(null);
  const [showPaymentManageModal, setShowPaymentManageModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  // Fetch membership plan from database
  const [membershipPlans, setMembershipPlans] = useState<MembershipPlan[]>([]);
  useEffect(() => {
    const fetchMembershipPlans = async () => {
      try {
        const plans = await paymentAPI.getMembershipPlan();
        setMembershipPlans(plans);
        // console.log("plans: ", plans);
      } catch (error: any) {
        console.error('Error fetching membership plans:', error);
      }
    };
    fetchMembershipPlans();
    fetchProfile();
  }, []);

  const handleAddLocationClick = () => {
    const currentLocations = (orgProfile?.businessLocations ?? organization.businessLocations ?? []);
    const maxLocations = (organization as any)?.membershipPlan?.maxLocations as MembershipPlan['maxLocations'] | null | undefined;

    const isUnlimited = maxLocations === 'unlimited' || maxLocations === null || maxLocations === undefined;
    const maxLocationsNumber = typeof maxLocations === 'number' ? maxLocations : Number(maxLocations);

    const hasReachedLimit =
      !isUnlimited &&
      Number.isFinite(maxLocationsNumber) &&
      currentLocations.length >= maxLocationsNumber;

    if (hasReachedLimit) {
      setAlertModal({
        open: true,
        title: 'Location limit reached',
        message: `Your current plan (${organization.membershipPlan?.name ?? 'Unknown'}) allows up to ${maxLocationsNumber} location${maxLocationsNumber === 1 ? '' : 's'}. Please upgrade your plan to add more locations.`,
        variant: 'info',
        confirmLabel: 'Change plan',
        onButton: () => {
          setAlertModal(prev => ({ ...prev, open: false }));

          // Upgrade / payment modals are rendered inside the Settings tab.
          setActiveTab('settings');
          setSelectedUpgradePlan(null);
          if ((organization.subscription?.subscription_id ?? '') === '') {
            setShowPaymentModal(true);
          } else {
            setShowUpgradeModal(true);
          }
        }
      });
      return;
    }

    setShowAddBusinessModal(true);
    setEditingBusiness(null);
  };

  // Fetch organization profile
  const fetchProfile = async () => {
    const token = localStorage.getItem('piezit-org-token');
    if (!token) return;

    try {
      const profile = await orgAPI.getProfile(token);
      if (!profile) return;

      const org = profile.org;
      // console.log("org: ", org);
      // Transform backend data to frontend format
      const businessLocations = (org.businessLocations || org.businesses || org.locations || []).map((b: any) => ({
        ...b,
        id: b.id,
        name: b.name,
        address: b.address,
        latitude: parseFloat(b.latitude),
        longitude: parseFloat(b.longitude),
        category: b.category,
        interestCategories: typeof b.interest_categories === 'string'
          ? JSON.parse(b.interest_categories)
          : b.interest_categories,
        tags: typeof b.tags === 'string' && b.tags
          ? JSON.parse(b.tags)
          : (b.tags || []),
        priceRange: b.price_range,
        phone: b.phone,
        website: b.website,
        description: b.description,
        averageRating: parseFloat(b.average_rating) || 0,
        totalReviews: parseInt(b.total_reviews) || 0,
        features: typeof b.features === 'string' && b.features
          ? JSON.parse(b.features)
          : (b.features || []),
        isActive: b.is_active ?? false,
        hours: b.hours
      }));

      setOrgProfile(org);

      const review = await reviewAPI.getByOrg(org.id); //pat
      // console.log('review: ', review);

      if (activeTab == 'analytics') {
        const review_summary = await reviewAPI.summaryByOrg(org.id);
        // console.log(review_summary);
        setSummaryReview(review_summary.result);
      }


      // Push the fresh profile up to the parent
      onUpdateProfile({
        id: org.id,
        orgName: org.orgName,
        ownerName: org.ownerName,
        email: org.email,
        phone: org.phone,
        website: org.website,
        description: org.description,
        category: org.category,
        businessLocations,
        membershipPlan: org.membershipPlan,
        subscription: org.subscription,
        isActive: org.is_actived,
        totalMonthlyFee: org.totalMonthlyFee ?? 0,
        analytics: org.analytics,
        reviews: review || [],
      });

      // console.log("memId: ", org.membershipPlan.id);

      setAccountForm({
        orgName: org.orgName || '',
        ownerName: org.ownerName || '',
        email: org.email || '',
        phone: org.phone || '',
        description: org.description || '',
      });

      // console.log("profileeee: ", org);
      // console.log("reviews: ", organization.analytics);
      setAllReviews(review || []);

    } catch (err) {
      console.error('Failed to fetch profile', err);
    }
  };


  useEffect(() => {
    fetchProfile();
  }, [activeTab]);


  // Location section - save business location
  const handleSaveBusiness = async (business: Partial<Business>) => {
    business.category = orgProfile.category || '';
    business.orgId = orgProfile.id;

    const businessId = business.id ?? '';
    if (!businessId) {
      const res = await api.business.create(business); console.log('Saving location', business);
    } else {
      const res = await api.business.update(businessId, business); console.log('Updating location', business);
    }
    setShowAddBusinessModal(false);
    setAlertModal({ open: true, title: "Success", message: "Business location saved successfully." });
    fetchProfile();
  };



  const handleEditBusiness = (business: Business) => {
    console.log('Editing business', business);
    setEditingBusiness(business);
    setShowAddBusinessModal(true);
  };

  const handleCancelSubscription = async () => {
    try {
      const result = await paymentAPI.cancelSubscription(organization.subscription?.subscription_id as string);
      console.log('handleCancelSubscription');
      console.log(result);
      if (result.id) {
        setAlertModal({ open: true, title: "Success", message: "Subscription canceled successfully." });
        setTimeout(() => fetchProfile(), 2000);
      }

    } catch (err) {
      console.error('Error cancel subscription', err);
    }
  };

  const handleRenewSubscription = async () => {
    try {
      const result = await paymentAPI.renewSubscription(organization.subscription?.subscription_id as string);
      console.log('handleRenewSubscription');
      console.log(result);
      if (result.id) {
        setAlertModal({ open: true, title: "Success", message: "Subscription renewed successfully." });
        setTimeout(() => fetchProfile(), 2000);
      }
    } catch (err) {
      console.error('Error renew subscription', err);
    }
  };


  const handleDeleteBusinessConfirm = async () => {
    if (!deleteBusiness) return;
    try {
      await api.business.delete(deleteBusiness.id);
      setShowDeleteBusiness(false);
      setDeleteBusiness(null);
      setAlertModal({ open: true, title: "Success", message: "Business location deleted successfully." });
      fetchProfile();
    } catch (err) {
      console.error('Error deleting business', err);
    }
  };



  const handleSubmitResponse = async () => {
    if (!responseModal.review) return;
    try {
      setSavingResponse(true);
      await api.review.addBusinessResponse(responseModal.review.id, responseModal.text);

      // update local state
      setAllReviews(prev =>
        prev.map(r =>
          r.id === responseModal.review.id
            ? { ...r, response: { content: responseModal.text, createdAt: new Date().toISOString() } }
            : r
        )
      );
      setResponseModal({ open: false, review: null, text: '' });
      setAlertModal({ open: true, title: "Success", message: "Response submitted successfully." });

      fetchProfile();
    } catch (err: any) {
      alert(err.message || 'Failed to save response');
    } finally {
      setSavingResponse(false);
    }
  };


  const handleConfirmUpgradePlan = async () => {
    if (!selectedUpgradePlan) return;
    try {
      await paymentAPI.upgradeSubscription(selectedUpgradePlan, organization.subscription?.subscription_id as string);
      setAlertModal({ open: true, title: "Success", message: "Upgrade plan successfully." });
      fetchProfile();
      setShowUpgradeModal(false);
    } catch (err: any) {
      console.error(err.message || 'Failed to update plan');
    }
  };


  const handleAccountChange = (field: keyof typeof accountForm, value: string) => {
    setAccountForm(prev => ({ ...prev, [field]: value }));
  };
  const handleSaveAccount = async () => {
    const token = localStorage.getItem('piezit-org-token');
    if (!token) return alert('Missing org token');

    try {
      setSavingAccount(true);
      await orgAPI.updateProfile(accountForm, token);
      setAlertModal({ open: true, title: "Success", message: "Account details updated successfully." });
      fetchProfile();

    } catch (err: any) {
      alert(err.message || 'Failed to update profile');
    } finally {
      setSavingAccount(false);
    }
  };




  const periodToRange = (period: string) => {
    // console.log('period', period);
    if (period === 'all') return {};
    const now = new Date();
    const d = new Date(now);
    if (period === '7d') d.setDate(d.getDate() - 7);
    if (period === '30d') d.setDate(d.getDate() - 30);
    if (period === '90d') d.setDate(d.getDate() - 90);
    if (period === '1y') d.setFullYear(d.getFullYear() - 1);
    const to = now.toISOString().slice(0, 10);
    const from = d.toISOString().slice(0, 10);
    console.log('from-to', from, to);
    return { from, to };
  };

  useEffect(() => {
    const load = async () => {
      const { from, to } = periodToRange(analyticsPeriod);
      const businessId = locationFilter === 'all'
        ? null
        : locationFilter;

      // Org-level stats when "all" is selected
      if (!businessId) {
        try {
          // adjust if your API signature differs
          const stats = await (reviewAPI as any)?.getOrgStats?.(orgProfile.id, from, to);
          setAnalyticsStats(stats || orgProfile.analytics || null);
        } catch (e) {
          console.error(e);
          setAnalyticsStats(orgProfile.analytics || null);
        }
        return;
      }

      // Business-level stats
      try {
        const stats = analyticsPeriod === 'all'
          ? await reviewAPI.getStats(businessId)
          : await reviewAPI.getStats(businessId, from, to);
        setAnalyticsStats(stats);
      } catch (e) {
        console.error(e);
        setAnalyticsStats(null);
      }
    };
    load();

    // console.log('Fetching analytics for', analyticsData);
  }, [analyticsPeriod, locationFilter, organization]);

  const analyticsData = analyticsStats || orgProfile?.analytics || {
    totalReviews: 0,
    averageRating: 0,
    ratingBreakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 },
    monthlyReviews: 0,
    reviewTrends: [],
    topKeywords: [],
    responseRate: 0,
    viewCount: 0,
    bookmarkCount: 0,
    favoriteCount: 0,
  };




  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now.getTime() - date.getTime());
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  // Use real analytics data from businessMember
  const analytics = orgProfile.analytics || {
    totalReviews: 0,
    averageRating: 0,
    monthlyReviews: 0,
    reviewTrends: [],
    topKeywords: [],
    responseRate: 0,
    viewCount: 0,
    bookmarkCount: 0,
    favoriteCount: 0,
    ratingBreakdown: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
  };


  const renderOverviewTab = () => (
    <div className="space-y-6">
      {/* Key Metrics Cards */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Reviews</p>
              <p className="text-3xl font-bold text-gray-900">{analyticsData.totalReviews || 0}</p>
              <p className="text-sm text-green-600 flex items-center mt-1">
                <TrendingUp className="w-4 h-4 mr-1" />
                +none this month
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <MessageCircle className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Average Rating</p>
              <p className="text-3xl font-bold text-gray-900">{parseFloat(analyticsData.averageRating).toFixed(1)}</p>
              <div className="flex items-center mt-1">
                {Array.from({ length: 5 }, (_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 ${i < Math.floor(analyticsData.averageRating)
                      ? 'text-yellow-400 fill-current'
                      : 'text-gray-300'
                      }`}
                  />
                ))}
              </div>
            </div>
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
              <Star className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Profile Views</p>
              <p className="text-3xl font-bold text-gray-900">{analyticsData.viewCount ?? 0}</p>
              <p className="text-sm text-green-600 flex items-center mt-1">
                <TrendingUp className="w-4 h-4 mr-1" />
                +none this month
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Eye className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Bookmarks</p>
              <p className="text-3xl font-bold text-gray-900">{analyticsData.bookmarkCount ?? 0}</p>
              <p className="text-sm text-green-600 flex items-center mt-1">
                <TrendingUp className="w-4 h-4 mr-1" />
                +none this month
              </p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Bookmark className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Review Trends */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Review Trends</h3>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value as any)}
              className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
            >
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
              <option value="90d">Last 90 days</option>
              <option value="1y">Last year</option>
            </select>
          </div>

          {/* Simple chart visualization */}
          <div className="space-y-3">
            {analyticsData.reviewTrends && analyticsData.reviewTrends.length > 0 ? (
              analytics.reviewTrends.map((trend, index) => {
                const maxCount = Math.max(...analytics.reviewTrends.map(t => t.count), 1);
                return (
                  <div key={`${trend.month}-${index}`} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600 w-20">{trend.month}</span>
                    <div className="flex-1 mx-4">
                      <div className="bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-primary-600 h-2 rounded-full"
                          style={{ width: `${(trend.count / maxCount) * 100}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-8">{trend.count}</span>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No review trends data available</p>
            )}
          </div>
        </div>

        {/* Rating Breakdown */}
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Rating Breakdown</h3>
          <div className="space-y-3">
            {analyticsData.totalReviews > 0 ? (
              Object.entries(analyticsData.ratingBreakdown).map(([rating, count]) => {
                const countNum = Number(count || 0);
                return (
                  <div key={rating} className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <Star className="w-4 h-4 text-yellow-400 fill-current" />
                      <span className="text-sm text-gray-700">{rating}</span>
                    </div>
                    <div className="flex-1 mx-4">
                      <div className="bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-yellow-400 h-2 rounded-full"
                          style={{ width: `${(countNum / analyticsData.totalReviews) * 100}%` }}
                        />
                      </div>
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-8">{countNum}</span>
                  </div>
                );
              })
            ) : (
              <p className="text-sm text-gray-500 text-center py-4">No rating breakdown data available</p>
            )}
          </div>
        </div>
      </div>

      {/* Recent Reviews */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Recent Reviews</h3>
          <button
            onClick={() => setActiveTab('reviews')}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            View All Reviews
          </button>
        </div>
        <div className="space-y-4">
          {allReviews.map((review) => (
            <div key={review.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center space-x-3">
                  <div className="w-8 h-8 bg-accent-600 rounded-full flex items-center justify-center">
                    <span className="text-white text-sm font-medium">
                      {review.user?.name?.charAt(0)}
                    </span>
                  </div>
                  <div>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-gray-900">{review.user?.name}</span>
                      {review.userVerified && (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      )}
                      <span className="font-medium text-gray-400"> - ({review.business_name})</span>
                    </div>
                    <div className="flex items-center space-x-1 mt-1">
                      {Array.from({ length: 5 }, (_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${i < review.rating
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                            }`}
                        />
                      ))}
                      <span className="text-sm text-gray-500 ml-1">{formatDate(review.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <h5 className="font-medium text-gray-900 mb-1">{review.title}</h5>
              <p className="text-gray-700 text-sm mb-3">{review.content}</p>

              {/* review photos */}
              {review.photos?.length > 0 && (
                <div className="mt-3 mb-3 flex flex-wrap gap-3">
                  {review.photos.map((url: string) => (
                    <button key={`btn${review.id}-${url}`} onClick={() => setActivePhoto({ url, reviewId: review.id })}
                      className="focus:outline-none">
                      <img
                        key={`${review.id}-${url}`}
                        src={url}
                        alt="Review photo"
                        className="w-20 h-20 object-cover rounded-lg border"
                      /></button>
                  ))}
                </div>
              )}

              {/* aspects rating */}
              {review.aspects?.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3 mb-3">
                  {review.aspects.map((aspect: any) => (
                    <div key={`${review.id}-${aspect.category}`} className="bg-gray-100 rounded-full px-3 py-1 text-xs flex">
                      <span className="capitalize">{aspect.category.replace('_', ' ')}</span>
                      <span className="ml-1 flex items-center gap-1">{aspect.rating}<Star className="w-2 h-2 text-yellow-400 fill-current" /></span>
                    </div>
                  ))}
                </div>
              )}

              {/* <div className="flex items-center justify-between">
                <button className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-800">
                  <Heart className="w-4 h-4" />
                  <span>Helpful ({review.helpful_count == 0 ? '0' : review.helpful_count})</span>
                </button>
                <button className="text-sm text-primary-600 hover:text-primary-700 font-medium">
                  Respond
                </button>
              </div> */}


            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderAnalyticsTab = () => (
    <div className="space-y-6">
      {/* Analytics Header */}
      <div className="md:flex items-center justify-between space-y-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Analytics & Insights</h2>
          <p className="text-gray-600">Detailed performance metrics for your business</p>
        </div>
        <div className="flex items-center justify-center space-x-3">
          <select
            value={locationFilter}
            onChange={(e) => setLocationFilter(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="all">All Locations</option>
            {(orgProfile?.businessLocations || organization.businessLocations || []).map((b: any) => (
              <option key={b.id} value={b.id}>{b.name || b.address}</option>
            ))}
          </select>
          <select
            value={analyticsPeriod}
            onChange={(e) => setAnalyticsPeriod(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-lg"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="1y">Last year</option>
          </select>
          {/* Export Report Button */}
          {/* <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2">
            <Download className="w-4 h-4" />
            <span>Export Report</span>
          </button> */}
        </div>
      </div>

      {/* Detailed Analytics Cards */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Review Summary</h3>
          <MessageCircle className="w-5 h-5 text-gray-400" />
        </div>
        <div className="space-y-2">
          {summaryReview ? (

            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-700">{summaryReview}</span>

            </div>

          ) : (
            <p className="text-sm text-gray-500 text-center py-2">No keywords data available</p>
          )}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Customer Engagement</h3>
            <BarChart3 className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Response Rate</span>
              <span className="text-sm font-medium text-green-600">{analyticsData.responseRate}%</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Avg. Response Time</span>
              <span className="text-sm font-medium text-gray-900">No data</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Customer Retention</span>
              <span className="text-sm font-medium text-gray-900">No data</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Popular Keywords</h3>
            <MessageCircle className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-2">
            {analytics.topKeywords && analytics.topKeywords.length > 0 ? (
              analytics.topKeywords.map((keyword, index) => (
                <div key={`${keyword}-${index}`} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">#{index + 1} {keyword}</span>
                  <span className="text-xs text-gray-500">
                    {Math.floor(Math.random() * 20) + 5} mentions
                  </span>
                </div>
              ))
            ) : (
              <p className="text-sm text-gray-500 text-center py-2">No keywords data available</p>
            )}
          </div>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-gray-900">Growth Metrics</h3>
            <TrendingUp className="w-5 h-5 text-gray-400" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">New Reviews</span>
              <span className="text-sm font-medium text-green-600">{analyticsData.totalReviews}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Profile Views</span>
              <span className="text-sm font-medium text-green-600">{analyticsData.viewCount ?? 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm text-gray-600">Favorite</span>
              <span className="text-sm font-medium text-green-600">{analyticsData.bookmarkCount ?? 0}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Competitor Comparison */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Competitor Benchmarking</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                <th className="text-left py-3 px-4 font-medium text-gray-700">Metric</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Your Business</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Industry Average</th>
                <th className="text-left py-3 px-4 font-medium text-gray-700">Top Competitor</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 text-gray-900">Average Rating</td>
                <td className="py-3 px-4 text-green-600 font-medium">{parseFloat(analyticsData.averageRating).toFixed(1)} ⭐</td>
                <td className="py-3 px-4 text-gray-600">No data ⭐</td>
                <td className="py-3 px-4 text-gray-600">No data ⭐</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 text-gray-900">Total Reviews</td>
                <td className="py-3 px-4 text-green-600 font-medium">{analyticsData.totalReviews}</td>
                <td className="py-3 px-4 text-gray-600">No data</td>
                <td className="py-3 px-4 text-gray-600">No data</td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="py-3 px-4 text-gray-900">Response Rate</td>
                <td className="py-3 px-4 text-green-600 font-medium">{analyticsData.responseRate}%</td>
                <td className="py-3 px-4 text-gray-600">No data</td>
                <td className="py-3 px-4 text-gray-600">No data</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderLocationsTab = () => (
    <div className="space-y-6">
      <div className="md:flex items-center justify-between space-y-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Locations</h2>
          <p className="text-gray-600">Manage your business locations and their performance</p>
        </div>
        <button
          onClick={handleAddLocationClick}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2">
          <Plus className="w-4 h-4" />
          <span>Add Location</span>
        </button>
      </div>


      {/* Locations List */}
      <div className="space-y-4">
        {(organization.businessLocations ?? []).map((business, index) => (
          <div key={business.id} className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <MapPin className="w-6 h-6 text-primary-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">
                    {business.name} {index === 0 && '(Primary)'}
                  </h3>
                  <p className="text-gray-600">{business.address}</p>
                  {business.phone && (
                    <p className="text-sm text-gray-500 mt-1">{business.phone}</p>
                  )}
                  <div className="flex items-center space-x-4 mt-2">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${business.isActive
                      ? 'bg-green-100 text-green-800'
                      : 'bg-red-100 text-red-800'
                      }`}>
                      {business.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => handleEditBusiness(business)}
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                  <Edit className="w-4 h-4" />
                </button>
                <div className="relative group hidden md:block">
                  <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                    <MoreHorizontal className="w-4 h-4" />
                  </button>
                  {/* Dropdown menu */}
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">


                    <div className="py-1">
                      <button onClick={() => { setDeleteBusiness(business); setShowDeleteBusiness(true); }}
                        className="flex items-center w-full px-4 py-2 gap-x-2 text-sm text-red-500 hover:bg-gray-50">
                        <Trash className='w-4 h-4' />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>


              </div>
            </div>

            {/* Location Performance */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-gray-200">
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{business.analytics?.averageRating}</div>
                <div className="text-sm text-gray-600">Rating</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{business.analytics?.totalReviews}</div>
                <div className="text-sm text-gray-600">Reviews</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{business.analytics?.viewCount}</div>
                <div className="text-sm text-gray-600">Views</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-gray-900">{business.analytics?.bookmarkCount}</div>
                <div className="text-sm text-gray-600">Favorite</div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>


  );

  const renderReviewsTab = () => (
    <div className="space-y-6">
      <div className="md:flex items-center justify-between space-y-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Customer Reviews</h2>
          <p className="text-gray-600">Manage and respond to customer reviews</p>
        </div>
        <div className="flex items-center space-x-3">
          <select
            value={ratingFilter}
            onChange={(e) => setRatingFilter(e.target.value as any)}
            className="px-4 py-2 border border-gray-300 rounded-lg">
            <option value="all">All Reviews</option>
            <option value="5">5 Stars</option>
            <option value="4">4 Stars</option>
            <option value="3">3 Stars</option>
            <option value="2">2 Stars</option>
            <option value="1">1 Star</option>
          </select>
          {/* Export button */}
          {/* <button className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2">
            <Download className="w-4 h-4" />
            <span>Export Reviews</span>
          </button> */}
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {filteredReviews.map((review) => (
          <div key={review.id} className="bg-white rounded-lg border border-gray-200 p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-accent-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {review.user?.name.charAt(0)}
                  </span>
                </div>
                <div>
                  <div className="md:flex items-center space-x-2">
                    <span className="font-medium text-gray-900">{review.user?.name}</span>
                    {review.userVerified && (
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    )}
                    <span className="text-gray-400">({review.business_name})</span>
                  </div>
                  <div className='flex flex-col md:flex-row'>
                    <div className="flex items-center space-x-1 mt-1">
                      {Array.from({ length: 5 }, (_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${i < review.rating
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300'
                            }`}
                        />
                      ))}
                    </div>
                    <span className="text-sm text-gray-500 md:ml-1 mt-1">{formatDate(review.created_at)}</span>
                  </div>
                </div>
              </div>



              <div className="flex items-center space-x-2">
                <button className="flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-800">
                  <Heart className="w-4 h-4" />
                  <span>{review.helpful_count == 0 ? '0' : review.helpful_count}</span>
                </button>
                {/* <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                  <MoreHorizontal className="w-4 h-4" />
                </button> */}
              </div>
            </div>

            <h5 className="font-medium text-gray-900 mb-2">{review.title}</h5>
            <p className="text-gray-700 mb-4">{review.content}</p>



            {/* review photos */}
            {review.photos?.length > 0 && (
              <div className="mt-3 mb-3 flex flex-wrap gap-3">
                {review.photos.map((url: string) => (
                  <button key={`btn${review.id}-${url}`} onClick={() => setActivePhoto({ url, reviewId: review.id })}
                    className="focus:outline-none">
                    <img
                      key={`${review.id}-${url}`}
                      src={url}
                      alt="Review photo"
                      className="w-20 h-20 object-cover rounded-lg border"
                    /></button>
                ))}
              </div>
            )}

            {/* aspects rating */}
            {review.aspects?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3 mb-3">
                {review.aspects.map((aspect: any) => (
                  <div key={`${review.id}-${aspect.category}`} className="bg-gray-100 rounded-full px-3 py-1 text-xs flex">
                    <span className="capitalize">{aspect.category.replace('_', ' ')}</span>
                    <span className="ml-1 flex items-center gap-1">{aspect.rating}<Star className="w-2 h-2 text-yellow-400 fill-current" /></span>
                  </div>
                ))}
              </div>
            )}

            {/* Business Response */}
            {review.business_response ? (
              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-primary-500">
                <div className="md:flex items-center md:space-x-2 mb-2">
                  <div className='flex space-x-2'>
                    <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                      <Building className="w-3 h-3 text-white" />
                    </div>
                    <span className="font-medium text-gray-900">Business Response</span>
                  </div>
                  <span className="text-sm text-gray-500">{formatDate(review.business_response_date)}</span>
                </div>
                <p className="text-gray-700">{review.business_response}</p>
              </div>
            ) : (
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => setResponseModal({ open: true, review, text: review.response?.content || '' })}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2">
                  <MessageCircle className="w-4 h-4" />
                  <span>Respond to Review</span>
                </button>
                {/* <button
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">
                  Mark as Helpful
                </button> */}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );

  const renderSettingsTab = () => (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Account Settings</h2>
        <p className="text-gray-600">Manage your subscription and account preferences</p>
      </div>

      {/* Subscription Info */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Subscription Details</h3>
          <span className={`px-3 py-1 rounded-full text-sm font-medium ${organization.subscription?.status === 'active'
            ? 'bg-green-100 text-green-800'
            : 'bg-red-100 text-red-800'
            }`}>
            {organization.subscription?.status || 'Inactive'}
          </span>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-600">Current Plan</label>
              <p className="font-medium text-gray-900">{organization.membershipPlan.name}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Monthly Fee</label>
              <p className="font-medium text-gray-900">${organization.totalMonthlyFee}</p>
            </div>
            <div>
              <label className="text-sm text-gray-600">Locations</label>
              <p className="font-medium text-gray-900">
                {organization.businessLocations?.length
                  ?? 0} / {organization.membershipPlan.maxLocations}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="text-sm text-gray-600">Subscription Date</label>
              <p className="font-medium text-gray-900">
                {organization.subscription?.current_period_start || 'Unpaid'}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-600">{organization.subscription?.cancel_at_period_end == false ? ('Next Billing') : ('Subscription End')}</label>
              <p className="font-medium text-gray-900">
                {organization.subscription?.current_period_end || 'Unpaid'}
              </p>
            </div>
          </div>
        </div>

        <div className="md:flex md:space-x-3 mt-6 space-y-3 md:space-y-0">
          {organization.subscription?.subscription_id === '' ? (<button
            onClick={() => {
              setShowPaymentModal(true);
              setSelectedUpgradePlan(null);
            }
            }
            className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 w-full md:w-auto ">
            Make a payment
          </button>) : (<><button
            onClick={() => {
              setShowUpgradeModal(true);
              setSelectedUpgradePlan(null);
            }
            }
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 w-full md:w-auto ">
            Upgrade Plan
          </button>
            <button
              onClick={() => setShowPaymentManageModal(true)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 w-full md:w-auto">
              Manage Billing
            </button></>)}




        </div>


      </div>

      {/* Account Information */}
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Account Information</h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Organization Name
            </label>
            <input
              type="text"
              onChange={(e) => handleAccountChange('orgName', e.target.value)}
              value={accountForm.orgName}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Owner Name
            </label>
            <input
              type="text"
              onChange={(e) => handleAccountChange('ownerName', e.target.value)}
              value={accountForm.ownerName}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              type="email"
              disabled
              value={accountForm.email}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone
            </label>
            <input
              type="tel"
              onChange={(e) => handleAccountChange('phone', e.target.value)}
              value={accountForm.phone}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description
          </label>
          <textarea
            onChange={(e) => handleAccountChange('description', e.target.value)}
            value={accountForm.description}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={handleSaveAccount}
            disabled={savingAccount}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
            {savingAccount ? 'Saving...' : 'Save Changes'}
          </button>
        </div>


        {showUpgradeModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Upgrade Plan</h3>
                <button onClick={() => setShowUpgradeModal(false)} className="text-gray-500">✕</button>
              </div>

              <p className="text-sm text-gray-600">
                Choose a plan that fits your business needs.
              </p>

              <div className="grid md:grid-cols-3 gap-6">
                {membershipPlans.map((plan) => {
                  const isSelected = selectedUpgradePlan === plan.id;
                  const locationCount = Math.max(
                    1,
                    (orgProfile?.businessLocations ?? organization.businessLocations ?? []).length
                  );
                  const maxLocations = Number(plan.maxLocations);
                  const canAccommodate =
                    plan.maxLocations === 'unlimited' || plan.maxLocations === null || locationCount <= maxLocations;
                  const monthlyEstimate = calculatePlanMonthlyEstimate(plan, locationCount);

                  return (

                    <div
                      key={plan.id}
                      onClick={() => canAccommodate && setSelectedUpgradePlan(plan.id)}
                      className={`relative flex min-h-full flex-col border-2 rounded-xl p-6 transition-all duration-200 ${isSelected
                        ? 'border-primary-500 bg-primary-50 shadow-lg'
                        : canAccommodate
                          ? 'border-gray-200 hover:border-primary-300 hover:shadow-md cursor-pointer'
                          : 'border-gray-200 opacity-50 cursor-not-allowed'}`}
                    >
                      {plan.popular && (
                        <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                          <span className="bg-primary-600 text-white px-3 py-1 rounded-full text-sm font-medium flex items-center space-x-1">
                            <Crown className="w-4 h-4" />
                            <span>Most Popular</span>
                          </span>
                        </div>
                      )}

                      <div className="text-center mb-6">
                        <h3 className="text-xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                        <div className="mb-2">
                          <span className="text-4xl font-bold text-gray-900">
                            {formatPlanCurrency(plan.pricePerLocation)}
                          </span>
                          <span className="text-base text-gray-600 font-normal">/location/mo</span>
                        </div>
                        <div className="inline-flex items-center rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                          {formatPlanLocationLimit(plan)}
                        </div>
                        <p className="text-sm text-gray-500 mt-2">{plan.description}</p>
                      </div>

                      <div className="mb-5 rounded-lg bg-white p-3 text-center shadow-sm">
                        <p className="text-xs uppercase tracking-wide text-gray-500">Estimated total</p>
                        <p className="text-lg font-semibold text-gray-900">
                          {canAccommodate ? formatPlanCurrency(monthlyEstimate) : 'Not available'}
                          {canAccommodate && <span className="text-sm font-normal text-gray-600">/mo</span>}
                        </p>
                        <p className="text-xs text-gray-500">
                          Based on {locationCount} location{locationCount === 1 ? '' : 's'}
                        </p>
                      </div>

                      <div className="flex-1 space-y-3">
                        {plan.features.map((feature, index) => (
                          <div key={index} className="flex items-start space-x-2">
                            <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{feature}</span>
                          </div>
                        ))}
                      </div>
                      {/* <input type="radio" name="plan" value={plan.id} /> */}

                      {!canAccommodate && (
                        <div className="absolute inset-0 bg-white bg-opacity-80 rounded-xl flex items-center justify-center">
                          <div className="text-center">
                            <p className="text-sm font-medium text-gray-700 mb-2">
                              Max {maxLocations} location{maxLocations === 1 ? '' : 's'}
                            </p>
                            <p className="text-xs text-gray-500">
                              Consider {plan.name === 'Starter' ? 'Professional' : 'Enterprise'} plan
                            </p>
                          </div>
                        </div>
                      )}


                      {isSelected && (
                        <div className="absolute top-4 right-4">
                          <div className="w-6 h-6 bg-primary-600 rounded-full flex items-center justify-center">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowUpgradeModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    // TODO: call backend to upgrade, then close
                    setShowUpgradeModal(false);
                    handleConfirmUpgradePlan();
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                >
                  Confirm Upgrade
                </button>
              </div>
            </div>
          </div>
        )}



        {showPaymentManageModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl p-6 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Manage Billing</h3>
                <button onClick={() => setShowPaymentManageModal(false)} className="text-gray-500">✕</button>
              </div>

              <p className="text-sm text-gray-600">
                Manage your subscription.
              </p>

              <div className='space-y-5 md:space-y-3'>
                <div className='md:flex gap-3 items-center'>
                  <button
                    onClick={() => {
                      setShowPaymentManageModal(false);
                      handleCancelSubscription();
                    }
                    }
                    disabled={organization.subscription?.cancel_at_period_end}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 w-full md:w-auto disabled:bg-gray-300 disabled:cursor-not-allowed">
                    Cancel Subscription
                  </button>
                  <p className="text-sm">Subscriptions can be canceled at the end of the current billing period.</p>
                </div>
                <div className='md:flex gap-3 items-center'>
                  <button
                    onClick={() => {
                      setShowPaymentManageModal(false);
                      handleRenewSubscription();
                    }
                    }
                    disabled={!organization.subscription?.cancel_at_period_end}
                    className="px-4 py-2 border border-primary-300 text-primary-700 rounded-lg hover:bg-primary-50 w-full md:w-auto disabled:bg-gray-300 disabled:cursor-not-allowed">
                    {organization.subscription?.cancel_at_period_end == true && organization.subscription.status == 'active' ? ('Resume Subscription') : ('Renew Subscription')}
                  </button>
                  <p className="text-sm">If a you have canceled the subscription, you can resume your subscription with the same plan.</p>
                </div>
              </div>


            </div>
          </div>
        )}

        {showPaymentModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900">
                  Make a Payment
                </h2>
                <button
                  onClick={() => setShowPaymentModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <CheckoutForm
                selectedPlanId={organization.membershipPlan.id}
                orgEmail={organization.email}
              />
            </div>
          </div>
        )}



      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="w-full mx-auto overflow-hidden md:flex">
        {/* Sidebar */}
        <div className="md:w-64 bg-white border-r border-gray-200 md:flex md:flex-col">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary-600 rounded-lg flex items-center justify-center">
                <Building className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-gray-900">{organization.orgName}</h2>
                <p className="text-sm text-gray-600">{organization.membershipPlan.name} Plan</p>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <nav className="md:flex-1 p-4">
            <div className="flex text-center md:flex-col space-y-1">
              {[
                { id: 'overview', label: 'Overview', icon: BarChart3 },
                { id: 'analytics', label: 'Analytics', icon: TrendingUp },
                { id: 'reviews', label: 'Reviews', icon: MessageCircle },
                { id: 'locations', label: 'Locations', icon: MapPin },
                { id: 'settings', label: 'Settings', icon: Settings }
              ].map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  onClick={() => setActiveTab(id as any)}
                  className={`w-full flex items-center justify-center md:justify-start space-x-3 px-3 py-2 rounded-lg  transition-colors duration-200 ${activeTab === id
                    ? 'bg-primary-100 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium hidden md:block">{label}</span>
                </button>
              ))}
            </div>
          </nav>



          {/* Close Button */}
          <div className="p-4 border-t border-gray-200 hidden md:block">
            <button
              onClick={onClose}
              className="w-full px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              Close Dashboard
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-8">
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'analytics' && renderAnalyticsTab()}
            {activeTab === 'reviews' && renderReviewsTab()}
            {activeTab === 'locations' && renderLocationsTab()}
            {activeTab === 'settings' && renderSettingsTab()}
          </div>

          {activePhoto && (
            <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6">
              <button
                className="absolute top-4 right-4 text-white text-2xl"
                onClick={() => setActivePhoto(null)}
              >
                ×
              </button>
              <img
                src={activePhoto.url}
                alt="Review photo"
                className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
              />
            </div>
          )}
        </div>
      </div>

      <AddBusinessLocationModal
        isOpen={showAddBusinessModal}
        onSubmit={(business) => { handleSaveBusiness(business); }}
        onCancel={() => { setShowAddBusinessModal(false) }}
        initial={editingBusiness ?? undefined}
      />

      {showDeleteBusiness && (
        <AlertModal
          open={true}
          title="Delete business?"
          message={deleteBusiness?.name || ''}
          variant="error"
          confirmLabel="Confirm"
          onClose={() => { setShowDeleteBusiness(false); setDeleteBusiness(null); }}
          onButton={() => { handleDeleteBusinessConfirm(); }}
        />
      )}

      {responseModal.open && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-lg p-6 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Respond to Review</h3>
              <button onClick={() => setResponseModal({ open: false, review: null, text: '' })} className="text-gray-500">✕</button>
            </div>
            <textarea
              className="w-full border rounded-lg p-3 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={5}
              value={responseModal.text}
              onChange={(e) => setResponseModal((prev) => ({ ...prev, text: e.target.value }))}
              placeholder="Write your response..."
            />
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setResponseModal({ open: false, review: null, text: '' })}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmitResponse}
                disabled={savingResponse || !responseModal.text.trim()}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60"
              >
                {savingResponse ? 'Saving...' : 'Send Response'}
              </button>
            </div>
          </div>
        </div>
      )}

      {alertModal.open && (
        <AlertModal
          open={alertModal.open}
          title={alertModal.title}
          message={alertModal.message}
          variant={alertModal.variant}
          confirmLabel={alertModal.confirmLabel}
          onButton={alertModal.onButton}
          onClose={() => setAlertModal(prev => ({ ...prev, open: false }))}
        />
      )}




    </div>
  );
};

export default BusinessDashboard;

